import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { PATIENT_NOTES } from "../constants/api";
import { MESSAGES } from "../constants/app";
import type { Note, PatientNoteView } from "../types";
import { apiUtils } from "../utils";

interface PatientNoteApiRecord {
  _id: string;
  patientUserId: string;
  text: string;
  doctorId: string;
  createdAt: string;
  updatedAt?: string | null;
}

const mapNoteToView = (note: PatientNoteApiRecord): PatientNoteView => ({
  id: note._id,
  text: note.text,
  date: new Date(note.createdAt),
  updatedAt: note.updatedAt ? new Date(note.updatedAt) : undefined,
});

/** Loads and mutates patient notes via helius-service. */
export function usePatientNotes(patientUserId?: string) {
  const [notes, setNotes] = useState<PatientNoteView[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNotes = useCallback(async () => {
    if (!patientUserId) {
      setNotes([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const requestOptions = apiUtils.createRequestOptions("GET");
      const response = await fetch(PATIENT_NOTES(patientUserId), requestOptions);
      const result = await apiUtils.handleResponse<{ data?: PatientNoteApiRecord[] }>(response);
      const rawNotes = Array.isArray(result?.data) ? result.data : [];
      setNotes(rawNotes.map(mapNoteToView));
    } catch (err: any) {
      const message = err?.message || MESSAGES.ERRORS.FETCH_NOTES;
      setError(message);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [patientUserId]);

  useEffect(() => {
    void fetchNotes();
  }, [fetchNotes]);

  const createNote = useCallback(async (text: string): Promise<boolean> => {
    if (!patientUserId || !text.trim()) return false;

    setSaving(true);
    setError(null);
    try {
      const requestOptions = apiUtils.createRequestOptions("POST", { text: text.trim() });
      const response = await fetch(PATIENT_NOTES(patientUserId), requestOptions);
      await apiUtils.handleResponse<Note>(response);
      await fetchNotes();
      return true;
    } catch (err: any) {
      const message = err?.message || "Failed to save note.";
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [fetchNotes, patientUserId]);

  const updateNote = useCallback(async (noteId: string, text: string): Promise<boolean> => {
    if (!patientUserId || !text.trim()) return false;

    setSaving(true);
    setError(null);
    try {
      const requestOptions = apiUtils.createRequestOptions("PUT", { text: text.trim() });
      const response = await fetch(
        `${PATIENT_NOTES(patientUserId)}/${noteId}`,
        requestOptions
      );
      await apiUtils.handleResponse<Note>(response);
      await fetchNotes();
      return true;
    } catch (err: any) {
      const message = err?.message || "Failed to update note.";
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [fetchNotes, patientUserId]);

  const deleteNote = useCallback(async (noteId: string): Promise<boolean> => {
    if (!patientUserId) return false;

    setDeleting(true);
    setError(null);
    try {
      const requestOptions = apiUtils.createRequestOptions("DELETE");
      const response = await fetch(
        `${PATIENT_NOTES(patientUserId)}/${noteId}`,
        requestOptions
      );
      await apiUtils.handleResponse(response);
      await fetchNotes();
      return true;
    } catch (err: any) {
      const message = err?.message || "Failed to delete note.";
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setDeleting(false);
    }
  }, [fetchNotes, patientUserId]);

  return {
    notes,
    loading,
    saving,
    deleting,
    error,
    fetchNotes,
    createNote,
    updateNote,
    deleteNote,
  };
}
